import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Upload, X } from "lucide-react";
import { api, API } from "@/lib/api";

export const AccountIdBanner = ({ user, setUser }) => {
  const [idAck, setIdAck] = useState(() => localStorage.getItem(`wtb_id_ack_${user.email}`) === "1");

  const idState =
    user.id_verified === "verified"
      ? "verified"
      : user.id_verified === "rejected"
        ? "rejected"
        : user.photo_id
          ? "pending"
          : "missing";

  if (idState === "verified" && idAck) return null;

  const reuploadId = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/photo-id", fd);
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success("Photo ID uploaded — pending verification");
      e.target.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  const ackId = () => {
    localStorage.setItem(`wtb_id_ack_${user.email}`, "1");
    setIdAck(true);
  };

  const hasActions = Boolean(user.photo_id) || idState !== "verified";

  return (
    <div
      data-testid="id-status-banner"
      className={`mt-8 rounded-2xl border px-5 py-4 ${
        idState === "verified"
          ? "border-emerald-200 bg-emerald-50"
          : idState === "rejected"
            ? "border-red-200 bg-red-50"
            : "border-amberwarm/30 bg-amberwarm/5"
      }`}
    >
      <div className="flex items-start gap-3">
        <ShieldCheck
          className={`mt-0.5 h-5 w-5 shrink-0 ${
            idState === "verified" ? "text-emerald-600" : idState === "rejected" ? "text-red-500" : "text-amberwarm"
          }`}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink" data-testid="id-status-text">
            {idState === "verified"
              ? "Photo ID verified"
              : idState === "rejected"
                ? "Photo ID couldn't be verified"
                : idState === "pending"
                  ? "Photo ID verification in progress"
                  : "Photo ID required to book"}
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
            {idState === "verified"
              ? "You're all set — bookings are confirmed without any verification hold."
              : idState === "rejected"
                ? "New bookings are on hold. Re-upload a valid ID (Aadhaar, PAN, Passport or Driving Licence) and we'll review it again."
                : idState === "pending"
                  ? "Bookings are subject to ID verification — if it can't be verified, the booking will be cancelled and you'll be notified."
                  : "Upload any government photo ID (Aadhaar, PAN, Passport or Driving Licence). Bookings stay disabled until one is on file and verified."}
          </p>
        </div>
      </div>

      {hasActions && (
        <div className="mt-3 flex flex-wrap gap-2 sm:pl-8">
          {user.photo_id && (
            <a
              href={`${API}/files/${user.photo_id.storage_path}`}
              target="_blank"
              rel="noreferrer"
              data-testid="id-view-current"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-ink/15 bg-white px-4 py-2 text-xs font-semibold text-navy transition-colors duration-200 hover:border-bark hover:text-bark"
            >
              View current ID
            </a>
          )}
          {idState !== "verified" && (
            <label
              data-testid="id-reupload-label"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-navy px-4 py-2 text-xs font-semibold text-cream transition-colors duration-200 hover:bg-ink"
            >
              <Upload className="h-3.5 w-3.5" aria-hidden="true" />
              {idState === "rejected" ? "Re-upload ID" : idState === "pending" ? "Replace ID" : "Upload ID"}
              <input data-testid="id-reupload-input" type="file" accept="image/*,.pdf" className="hidden" onChange={reuploadId} />
            </label>
          )}
          {idState === "verified" && (
            <button
              type="button"
              data-testid="id-ack-button"
              onClick={ackId}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-emerald-700"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
              Got it
            </button>
          )}
        </div>
      )}
    </div>
  );
};
